import { VariableIcon } from '@heroicons/react/24/outline';
import { useDispatch } from 'react-redux';
import { setCurrentEditingField } from '../../store/slice';
import { CWTSchemaPropertySummary, FieldType, TextAlignment, WorksheetSchemaField } from '../../../../../types/custom-worksheet-v2';
import { useRef } from 'react';
import { isField } from '../../store/utils';
import { TooltipV2 } from '@cimplico/components';
import { useWorkpaperContext } from '../../../../../providers/WorkpaperProvider';
import CalculationOverride from './CalculationOverride';
import FieldIcon from '../../../../../components/custom-worksheet/FieldIcon';
import useIsClamped from '../../../../../hooks/use-line-clamp-active';
import Currency from '../../../../../components/currency/Currency';
import NavItem from '../../tree/NavItem';

type CWReadFieldProps = {
    field: WorksheetSchemaField | CWTSchemaPropertySummary;
    showIcon: boolean;
    alignment?: TextAlignment;
    mode?: 'row' | 'keyValue';
    allowRedirect?: boolean;
};

function CWReadField({ field, showIcon, alignment = 'left', mode, allowRedirect = false }: CWReadFieldProps): JSX.Element {
    const { readOnly } = useWorkpaperContext();
    const dispatch = useDispatch();

    // Field is disabled if readOnly is true or field is a summary value
    const disabledField = ('readOnly' in field && field.readOnly) || !('readOnly' in field);

    const readField = (
        <div className="flex py-0.5 w-full items-center group relative">
            <div
                className={`flex flex-1 gap-2 py-1.5 rounded-[5px] items-center w-full border-transparent border ${
                    disabledField || field.calculation ? 'cursor-not-allowed bg-gray-50 opacity-70' : 'cursor-text hover:bg-gray-100'
                } ${mode === 'keyValue' ? 'pr-2 pl-3' : ''}`}
                onClick={(): void => {
                    if (!field.calculation && !disabledField && !readOnly) {
                        dispatch(setCurrentEditingField(field.id));
                    }
                }}
            >
                {showIcon && (
                    <div className="shrink-0 w-4 h-4 text-gray-500">
                        <FieldIcon type={field.type} />
                    </div>
                )}
                <div className="flex items-center gap-1 text-sm w-full">
                    {field.value !== null && (
                        <div style={{ textAlign: alignment }} className="w-full">
                            <FieldValue type={field.type} value={field.value} />
                        </div>
                    )}
                    {field.value === null && (
                        <p
                            style={{ textAlign: alignment }}
                            className={`${field.calculation === null ? 'text-gray-900' : 'text-gray-500'} w-full`}
                        >
                            {field.calculation === null ? '-' : 'Waiting...'}
                        </p>
                    )}
                    {field.calculation !== null && <VariableIcon className="w-4 h-4 ml-auto text-gray-500 shrink-0" />}
                </div>
            </div>
        </div>
    );

    // If property is a WorksheetSchemaField, allow calculation override
    if ('order' in field && isField(field) && !allowRedirect) {
        return <CalculationOverride field={field}>{readField}</CalculationOverride>;
    }

    if (allowRedirect) {
        return <NavItem id={field.id}>{readField}</NavItem>;
    }

    return <>{readField}</>;
}

export default CWReadField;

type FieldValueProps = {
    type: FieldType;
    value: number | string;
};

export function FieldValue({ type, value }: FieldValueProps): JSX.Element {
    const textElementRef = useRef(null);
    const isClamped = useIsClamped(textElementRef);

    if (value === '') {
        return <>-</>;
    }

    switch (type) {
        case FieldType.ACCOUNTING:
            return <Currency className="text-gray-900" value={Math.abs(value as number)} withParentheses={(value as number) < 0} />;
        case FieldType.DATE:
            return <p className="text-gray-900">{new Date(value).toLocaleDateString('en-AU')}</p>;
        case FieldType.MONEY:
            return <Currency className="text-gray-900" value={value as number} />;
        case FieldType.NUMBER:
        case FieldType.TEXT:
            return (
                <TooltipV2 message={value.toString()} disabled={!isClamped}>
                    <p ref={textElementRef} className="text-gray-900 line-clamp-1 break-words">
                        {value}
                    </p>
                </TooltipV2>
            );
        case FieldType.PERCENT:
            return (
                <p className="text-gray-900">
                    {new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format((value as number) * 100) + ' %'}
                </p>
            );

        default:
            return <></>;
    }
}
